/**
 * 动作录制与批处理内核（M4-04 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 只录制/回放/序列化带 `Command.record` 字段的命令（ADR-0007）——画笔描边、
 * 新建/删除图层这类携带像素或子树的命令没有这个字段，录制时被跳过并计数，
 * 不是被静默丢弃：`stop()` 的返回值里 `skipped` 就是跳过了多少步。
 */
import type { Command, CommandListener, Json } from '../command-bus';
import { createSetCanvasSizeCommand } from '../commands/canvas-commands';
import { createSetAdjustmentCommand } from '../commands/adjustment-commands';
import {
  createRenameLayerCommand,
  createSetVisibleCommand,
  createSetOpacityCommand,
  createSetBlendModeCommand,
  createSetTransformCommand,
  createSetEffectsCommand,
} from '../commands/layer-commands';
import {
  createRemoveMaskCommand,
  createSetMaskEnabledCommand,
  createSetClippedCommand,
  createFlipMaskBaseCommand,
} from '../commands/mask';
import { createSetTextCommand, createSetWritingModeCommand } from '../commands/text-commands';
import { createSetShapePathCommand } from '../commands/shape-commands';
import { createSetSmartFiltersCommand } from '../commands/smart-filter-commands';
import {
  createAddLayerCompCommand,
  createApplyLayerCompCommand,
  createRemoveLayerCompCommand,
} from '../commands/layer-comp-commands';
import type {
  Adjustment,
  BlendMode,
  LayerComp,
  LayerEffect,
  LayerId,
  Mat3,
  PathData,
  Rect,
  SmartFilter,
  TextRun,
} from '../../document/types';

export interface RecordedStep {
  readonly kind: string;
  readonly args: Readonly<Record<string, Json>>;
}

export interface RecordedAction {
  readonly name: string;
  readonly steps: readonly RecordedStep[];
}

/** 每种可录制命令的解码器：拿回 `record.args`，重建出一条可派发的 `Command`。 */
type Decoder = (args: Readonly<Record<string, Json>>) => Command;

function str(v: Json | undefined, field: string): string {
  if (typeof v !== 'string') throw new RangeError(`动作步骤缺少字符串字段 ${field}。`);
  return v;
}
function num(v: Json | undefined, field: string): number {
  if (typeof v !== 'number') throw new RangeError(`动作步骤缺少数字字段 ${field}。`);
  return v;
}
function bool(v: Json | undefined, field: string): boolean {
  if (typeof v !== 'boolean') throw new RangeError(`动作步骤缺少布尔字段 ${field}。`);
  return v;
}

const DECODERS: Readonly<Record<string, Decoder>> = {
  'canvas.setSize': (a) => createSetCanvasSizeCommand(num(a['width'], 'width'), num(a['height'], 'height')),
  'adjustment.set': (a) =>
    createSetAdjustmentCommand(str(a['id'], 'id') as LayerId, a['adjustment'] as unknown as Adjustment),
  'layer.rename': (a) => createRenameLayerCommand(str(a['id'], 'id') as LayerId, str(a['name'], 'name')),
  'layer.visible': (a) =>
    createSetVisibleCommand(str(a['id'], 'id') as LayerId, bool(a['visible'], 'visible')),
  'layer.opacity': (a) =>
    createSetOpacityCommand(str(a['id'], 'id') as LayerId, num(a['opacity'], 'opacity')),
  'layer.blendMode': (a) =>
    createSetBlendModeCommand(str(a['id'], 'id') as LayerId, str(a['blendMode'], 'blendMode') as BlendMode),
  'layer.transform': (a) =>
    createSetTransformCommand(str(a['id'], 'id') as LayerId, a['transform'] as unknown as Mat3),
  'layer.effects': (a) =>
    createSetEffectsCommand(str(a['id'], 'id') as LayerId, a['effects'] as unknown as readonly LayerEffect[]),
  'mask.remove': (a) => createRemoveMaskCommand(str(a['id'], 'id') as LayerId),
  'mask.enable': (a) =>
    createSetMaskEnabledCommand(str(a['id'], 'id') as LayerId, bool(a['enabled'], 'enabled')),
  'layer.clip': (a) =>
    createSetClippedCommand(str(a['id'], 'id') as LayerId, bool(a['clipped'], 'clipped')),
  'mask.flipBase': (a) => createFlipMaskBaseCommand(str(a['id'], 'id') as LayerId),
  'text.set': (a) =>
    createSetTextCommand(
      str(a['id'], 'id') as LayerId,
      a['runs'] as unknown as readonly TextRun[],
      a['box'] as unknown as Rect,
    ),
  'text.writingMode': (a) =>
    createSetWritingModeCommand(
      str(a['id'], 'id') as LayerId,
      str(a['writingMode'], 'writingMode') as 'horizontal' | 'vertical',
    ),
  'shape.setPath': (a) =>
    createSetShapePathCommand(str(a['id'], 'id') as LayerId, a['path'] as unknown as PathData),
  'layerComp.add': (a) => createAddLayerCompCommand(a['comp'] as unknown as LayerComp),
  'layerComp.remove': (a) => createRemoveLayerCompCommand(str(a['id'], 'id')),
  'layerComp.apply': (a) => createApplyLayerCompCommand(str(a['id'], 'id')),
  'smartObject.setFilters': (a) =>
    createSetSmartFiltersCommand(str(a['id'], 'id') as LayerId, a['filters'] as unknown as readonly SmartFilter[]),
};

/** 把一条录制步骤解码回可派发的 `Command`。未知种类抛错——不是"跳过"，是数据本身坏了。 */
export function decodeStep(step: RecordedStep): Command {
  const decoder = DECODERS[step.kind];
  if (decoder === undefined) throw new RangeError(`未知的动作步骤种类：${step.kind}。`);
  return decoder(step.args);
}

/**
 * 能被订阅命令流的东西——`CommandBus` 满足它，但录制器不必知道具体类型
 * （`AppStore` 只暴露订阅、不暴露 `dispatch`，见 store.ts 的 `commandBus` 访问器：
 * 录制只需要"看"，修改文档必须仍然只走 `store.dispatch`）。
 */
export interface CommandSource {
  subscribe(listener: CommandListener): () => void;
}

/**
 * 动作录制器：订阅命令流，把带 `record` 字段的命令收集成步骤序列。
 * 一次只能录一个动作——`start` 时若已在录制会抛错，避免混进两段历史。
 */
export class ActionRecorder {
  #steps: RecordedStep[] | null = null;
  #skipped = 0;
  #unsubscribe: (() => void) | null = null;

  get isRecording(): boolean {
    return this.#steps !== null;
  }

  get stepCount(): number {
    return this.#steps?.length ?? 0;
  }

  start(source: CommandSource): void {
    if (this.#steps !== null) throw new Error('已经在录制中，先 stop() 再开始新的录制。');
    this.#steps = [];
    this.#skipped = 0;
    this.#unsubscribe = source.subscribe((result) => {
      if (!result.changed) return;
      const record = result.command.record;
      if (record === undefined) {
        this.#skipped++;
        return;
      }
      this.#steps!.push({ kind: record.kind, args: record.args });
    });
  }

  /** 停止录制，返回具名动作。`skipped` 是因为携带像素/子树而未被录下的步数（ADR-0007）。 */
  stop(name: string): { readonly action: RecordedAction; readonly skipped: number } {
    if (this.#steps === null) throw new Error('当前没有在录制。');
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    const action: RecordedAction = { name, steps: this.#steps };
    const skipped = this.#skipped;
    this.#steps = null;
    this.#skipped = 0;
    return { action, skipped };
  }

  cancel(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#steps = null;
    this.#skipped = 0;
  }
}

/**
 * 回放：把动作的每一步解码为命令，依次交给 `dispatch` 执行。
 *
 * 接受 `dispatch` 函数而不是 `CommandBus`——应用内回放要用
 * `store.dispatch`（走历史/撤销），批处理要用一条性 `CommandBus.dispatch`
 * （没有历史），两者都满足 `(command: Command) => void`，没必要为此暴露
 * `store` 内部的 bus。
 */
export function replayAction(dispatch: (command: Command) => void, action: RecordedAction): void {
  for (const step of action.steps) {
    dispatch(decodeStep(step));
  }
}

export function serializeAction(action: RecordedAction): string {
  return JSON.stringify(action);
}

/** 反序列化：结构不对（不是对象/name 不是字符串/steps 不是数组）一律抛错，不猜测。 */
export function deserializeAction(json: string): RecordedAction {
  const raw: unknown = JSON.parse(json);
  if (typeof raw !== 'object' || raw === null) throw new RangeError('动作 JSON 不是对象。');
  const obj = raw as Record<string, unknown>;
  if (typeof obj['name'] !== 'string') throw new RangeError('动作 JSON 缺少 name 字段。');
  if (!Array.isArray(obj['steps'])) throw new RangeError('动作 JSON 缺少 steps 数组。');
  const steps: RecordedStep[] = obj['steps'].map((raw, i) => {
    if (typeof raw !== 'object' || raw === null) throw new RangeError(`动作步骤 #${i} 不是对象。`);
    const step = raw as Record<string, unknown>;
    if (typeof step['kind'] !== 'string') throw new RangeError(`动作步骤 #${i} 缺少 kind。`);
    if (typeof step['args'] !== 'object' || step['args'] === null) {
      throw new RangeError(`动作步骤 #${i} 缺少 args。`);
    }
    return { kind: step['kind'], args: step['args'] as Readonly<Record<string, Json>> };
  });
  return { name: obj['name'], steps };
}
